import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

import { MenuItem, ConfirmationService, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { PROVIDERS, countAccountsForConnectProvider } from '@admin/shared/constants/constant';
import { WorkspaceConnectionService } from '@admin/shared/services/workspaceConnection.service';

import { UserProfileService } from '@core/services/user.service';
import { ModalServiceService } from '@core/services/modal-service.service';

import { environment } from '@environment/environment';

import { OnboardingService } from '../shared/services/onboarding.service';
import { SocialPopupLinkedinComponent } from '../social-popup-linkedin/social-popup-linkedin.component';
import { ConnectionListPopupComponent } from '../connection-list-popup/connection-list-popup.component';

@Component({
  selector: ' app-onboarding-step-four',
  templateUrl: './onboarding-step-four.component.html',
  styleUrl: './onboarding-step-four.component.scss',
  providers: [ConfirmationService, MessageService],
  standalone: false,
})
export class OnboardingStepFourComponent {
  constructor(
    private onboardingService: OnboardingService,
    private userProfileService: UserProfileService,
    private modalService: ModalServiceService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private workspaceConnectionService: WorkspaceConnectionService,
    private router: Router
  ) {}
  items: MenuItem[] | undefined;
  @Input() workspaceDetails: any = [];
  @Output() nextStep = new EventEmitter<any>(false);
  connectionList: any;
  connectedAccounts: any[] = [];
  requestId = '';
  provider = '';
  connectionWindow: Window | any;
  showBackdrop = false;
  protected readonly environment = environment;
  protected readonly providers = PROVIDERS;
  private destroy$ = new Subject<void>();
  preventAbuse = false;

  responsiveOptions = [
    {
      breakpoint: '1199px',
      numVisible: 1,
      numScroll: 1,
    },
    {
      breakpoint: '991px',
      numVisible: 2,
      numScroll: 1,
    },
    {
      breakpoint: '767px',
      numVisible: 1,
      numScroll: 1,
    },
  ];
  workspacePermission: any;
  openLinkedinRef: DynamicDialogRef | undefined;
  openSelectPagesRef: DynamicDialogRef | undefined;
  ngOnInit() {
    this.items = [
      {
        label: 'Delete',
        icon: 'pi pi-times',
      },
    ];
  }

  clickConnect(event: { preventDefault: () => void }, value: any) {
    event.preventDefault();
    this.connectToProvider(value);
  }

  connectToProvider(value: any) {
    if (!(value && value.type && value.provider)) return;
    this.provider = value.provider;
    this.onboardingService.connectToAccount(value.provider, value.type)?.subscribe({
      next: (res: any) => {
        let loopCount = 180;

        const width = window.innerWidth * 0.8;
        const height = window.innerHeight * 0.8;

        // Calculate the top and left position to center the popup window
        const left = (window.innerWidth - width) / 2;
        const top = (window.innerHeight - height) / 2;

        // Open the popup window
        this.connectionWindow = window.open(
          res['auth-url'],
          'popup',
          `popup=true,left=${left},top=${top},width=${width},height=${height}`
        );
        // show backdrop
        this.showBackdrop = true;
        // Focus on the popup window (optional)
        if (this.connectionWindow) {
          this.connectionWindow.focus();
        }

        const checkPopup = setInterval(() => {
          try {
            if (this.connectionWindow.closed) {
              this.showBackdrop = false;
              clearInterval(checkPopup);
            } else {
              let href = this.connectionWindow?.window.location.href;
              if (href && href.includes('social/callback')) {
                href = href.replace(environment.frontendDomain + 'social/', '');
                this.onboardingService.registerCallbackURL(href)?.subscribe({
                  next: (innerRes: any) => {
                    if (value.type === 'page') {
                      if (innerRes.status) {
                        this.openSelectPagesDialog(
                          innerRes.requestId,
                          this.provider,
                          innerRes.entities
                        );
                      } else {
                        this.modalService.openErrorModal(
                          '<strong>' +
                            innerRes.message +
                            '</strong><br/> For immediate assistance, please visit <a href="https://support.confe.io" target="_blank" rel="noopener noreferrer">support.confe.io</a>.'
                        );
                      }
                    } else {
                      this.userProfileService
                        .getUserDetails()
                        .pipe(takeUntil(this.destroy$))
                        .subscribe();
                      this.modalService.openSuccessModal(
                        'Connection successful',
                        'Your account is successfully connected!'
                      );
                    }
                  },
                  error: (err) => {
                    this.showBackdrop = false;
                    this.modalService.openErrorModal(
                      err?.error?.error?.replace('\n', ' ') ||
                        err?.error?.message ||
                        'Something went wrong. Please try again'
                    );
                  },
                });
                this.connectionWindow?.close();
                this.showBackdrop = false;
                clearInterval(checkPopup);
              }
            }
          } catch (e) {
            // Ignore cross-origin access errors
          }
          if (loopCount-- < 0) {
            this.connectionWindow?.close();
            this.showBackdrop = false;
            clearInterval(checkPopup);
          }
        }, 1000);
      },
      error: (err) => {
        this.modalService.openErrorModal(err.error.error.replace('\\n', ' '));
      },
    });
  }

  showConnectionWindow() {
    if (this.connectionWindow && !this.connectionWindow.closed) {
      this.connectionWindow.focus(); // Focus on existing popup if it's open
    } else {
      this.showBackdrop = false;
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['workspaceDetails'] && changes['workspaceDetails'].currentValue) {
      this.getConnectedAccounts(changes['workspaceDetails'].currentValue);
      this.workspacePermission = changes['workspaceDetails'].currentValue.limits;
    }
  }
  getConnectedAccounts(workspace: any) {
    if (workspace) {
      this.connectedAccounts = workspace.connected_accounts;
    }
  }

  deleteAccount(event: Event, accountUUID: string) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Do you want to disconnect this account?',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.delete(accountUUID, true);
      },
      reject: () => {},
    });
  }

  delete(uuid: string = '', showDeleteAlert: boolean) {
    const workspaceId = this.workspaceDetails.uuid;
    this.workspaceConnectionService.deleteConnectedAccount(workspaceId, uuid).subscribe({
      next: () => {
        this.userProfileService.getUserDetails().subscribe();
        if (showDeleteAlert) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Account disconnected successfully!',
          });
        }
      },
      error: (err) => {
        this.modalService.openErrorModal(err.message);
      },
    });
  }

  setOnboardingCompleted() {
    // move to dashboard
    this.preventAbuse = true;
    this.onboardingService.setOnboardingForUser().subscribe({
      next: () => {
        this.userProfileService
          .getUserDetails()
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.router.navigateByUrl('/');
            this.preventAbuse = false;
          });
      },
      error: (err) => {
        this.modalService.openErrorModal(err.message);
        this.preventAbuse = false;
      },
    });
    // this.nextStep.emit({ moveToNextStep: true, updateUser: false });
  }

  isTotalConnectionMaxLimitReached(): boolean {
    const totalAccountLimits = this.workspacePermission?.NumberOfSocialAccounts || 0;
    return this.connectedAccounts.length >= totalAccountLimits;
  }

  isConnectionByBrandMaxLimitReached(provider: string): boolean {
    const connectionPerAccount = this.workspacePermission?.NumberOfBrandsSocialAccounts || 0;
    const perBrandAdded = countAccountsForConnectProvider(this.connectedAccounts, provider);
    return perBrandAdded >= connectionPerAccount;
  }

  openLinkedinDialog() {
    this.openLinkedinRef = this.dialogService.open(SocialPopupLinkedinComponent, {
      width: '650px',
      breakpoints: { '1199px': '75vw', '575px': '90vw' },
    });
    this.openLinkedinRef.onClose.subscribe((result: any) => {
      if (result) {
        this.connectToProvider(result); // Do something with the emitted data
      }
    });
  }

  openSelectPagesDialog(requestId: string, provider: string, connectionList: []) {
    const perBrandAdded = countAccountsForConnectProvider(this.connectedAccounts, provider);
    this.openSelectPagesRef = this.dialogService.open(ConnectionListPopupComponent, {
      width: '1150px',
      breakpoints: { '1199px': '75vw', '575px': '90vw' },
      data: {
        requestId: requestId,
        provider: provider,
        connectionList: connectionList,
        totalConnectedAccountsAllowed: this.workspacePermission.NumberOfSocialAccounts,
        totalConnectedAccountsTillNow: this.connectedAccounts.length,
        totalAllowedPerBrand: this.workspacePermission.NumberOfBrandsSocialAccounts,
        perBrandAddedForCurrentProvider: perBrandAdded,
        selectedWorkspaceDetails: this.workspaceDetails,
      },
    });
    this.openSelectPagesRef.onClose.subscribe((result: any) => {
      // Delete if connection flow not completed!
      if (!result && requestId != '') this.delete(requestId, false);
      if (result && result.connected) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Account connected successfully!',
        });
      }
      this.userProfileService.getUserDetails().subscribe();
    });
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  protected readonly PROVIDERS = PROVIDERS;
}
